/**
 * @module LineChart
 * @description
 * Wrapper around chart.js, to render a custom line chart.
 */
import Chart from 'chart.js';
import './line_with_hover';
import renderChartTooltip from './render_chart_tooltip';

const DEFAULT_LINE_PROPS = {
  fill: false,
  lineTension: 0,
};

export default class {
  /**
   * @param {HTMLCanvasElement} canvas - the target canvas element's context to render the chart within
   * @param {Object[]} series - array of chart series'
   * @param {string[]} labels - collection of labels for the chart
   * @param {Object} chartOptions - an object containing overall chart options
   */
  constructor(canvas, series, labels, chartOptions) {
    this.canvas = canvas;
    this.series = series;
    this.labels = labels;
    this.chartOptions = chartOptions;
    this.chart = null;

    this.#renderLineChart();
    this.removeTooltip = this.removeTooltip.bind(this);
    document.body.addEventListener('transitionend', this.removeTooltip, false);
  }

  destroy() {
    if (this.chart) {
      this.chart.destroy();
    }
    document.body.removeEventListener('transitionend', this.removeTooltip);
  }

  removeTooltip({ target }) {
    if (target.id !== 'chart-tooltip') return;

    if (parseFloat(getComputedStyle(target).opacity) <= 0) {
      target.remove();
    }
  }

  /**
   * Builds the chart with provided config options. A `type` of `large` will render a chart suited
   *  for larger displays.
   */
  #renderLineChart() {
    this.chart = new Chart(this.canvas, {
      type: this.chartOptions?.type === 'large' ? 'LineWithHover' : 'line',
      data: {
        labels: this.labels,
        datasets: this.#formattedDatasets,
      },
      options: this.#options,
    });
  }

  update(options = null) {
    if (options) this.chartOptions = options;
    this.chart.options = this.#options;
    this.chart.update();
  }

  /** Bake in some line formatting with each data series. */
  get #formattedDatasets() {
    return this.series.map(({ values, options: { borderColor, borderWidth, pointRadius }}) => ({
      ...DEFAULT_LINE_PROPS,
      data: values,
      borderColor,
      backgroundColor: borderColor,
      borderWidth,
      pointRadius,
    }));
  }

  get #options() {
    return this.chartOptions?.type === 'large' ? this.#largeOptions : this.#smallOptions;
  }

  /** Chart options for the small chart. */
  get #smallOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false,
      },
      tooltips: {
        enabled: false,
      },
      scales: {
        xAxes: [{
          display: false,
          gridLines: {
            display: false,
          },
        }],
        yAxes: [{
          position: 'right',
          gridLines: {
            display: false,
          },
          ticks: {
            // allow overriding the axis label color
            fontColor: this.chartOptions?.axisLabelColor || '#989799',
          },
          afterBuildTicks: (chart) => {
            // allow overriding of the tick labels that are displayed
            chart.ticks = this.chartOptions?.overrideTicks || [];
          },
        }]
      }
    };
  }

  /** Chart options for the large chart. */
  get #largeOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false,
      },
      tooltips: {
        enabled: false, // disabled default canvas tooltips
        intersect: false, // use by LineWithHover type for rendering hover line
        custom: renderChartTooltip
      },
      scales: {
        xAxes: [{
          gridLines: {
            color: '#605F60',
            tickMarkLength: 5,
            zeroLineWidth: 0,
          },
          ticks: {
            display: false
          },
        }],
        yAxes: [{
          type: 'linear',
          position: 'left',
          gridLines: {
            color: '#605F60',
            tickMarkLength: 5,
            zeroLineWidth: 1,
            zeroLineColor: '#605F60',
          },
          ticks: {
            fontColor: '#989799',
            padding: 10,
            lineHeight: 1.6,
          },
          afterFit: (scaleInstance) => {
            scaleInstance.width = 60; // sets the label width to 60px
          },
        }]
      }
    };
  }

}
